'use client';
import React, { useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import {
  Camera,
  MessageSquare,
  User,
  LayoutDashboard,
  Zap,
  TrendingUp,
  Shield,
  ChevronRight,
  Star,
  Clock,
  DollarSign,
  CheckCircle,
  ArrowRight,
  BarChart2,
  Filter,
  Settings2,
} from 'lucide-react';

/* ─── tiny hook: animate number counting up ─── */
function useCountUp(target: number, duration = 1200, start = false) {
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (!start) return;
    let startTime: number | null = null;
    const step = (ts: number) => {
      if (!startTime) startTime = ts;
      const progress = Math.min((ts - startTime) / duration, 1);
      setValue(Math.floor(progress * target));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [target, duration, start]);
  return value;
}

/* ─── intersection observer hook ─── */
function useInView(threshold = 0.2) {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setInView(true); }, { threshold });
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return { ref, inView };
}

/* ─── stat counter card ─── */
function StatCard({ value, suffix, label, start }: { value: number; suffix: string; label: string; start: boolean }) {
  const count = useCountUp(value, 1400, start);
  return (
    <div className="flex flex-col items-center">
      <span className="text-4xl font-800 text-[#FF6B35] tabular-nums">
        {count}{suffix}
      </span>
      <span className="text-sm text-[#666] mt-1 text-center">{label}</span>
    </div>
  );
}

export default function LandingPage() {
  const { ref: statsRef, inView: statsVisible } = useInView(0.3);

  return (
    <div className="min-h-screen bg-[#050505] text-white overflow-x-hidden">

      {/* ── NAV ── */}
      <nav className="fixed top-0 left-0 right-0 z-50 flex items-center justify-between px-6 md:px-12 h-16 bg-[#050505]/80 backdrop-blur-md border-b border-[#1A1A1A]">
        <div className="flex items-center gap-2.5">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img src="/assets/images/quotes-1782409512552.png" alt="Serious Quotes logo" className="h-9 w-auto" />
        </div>
        <div className="flex items-center gap-3">
          <Link href="/sign-up-login-screen" className="text-sm text-[#888] hover:text-white transition-colors duration-150 font-500">
            Sign In
          </Link>
          <Link href="/sign-up-login-screen" className="leverage-btn-primary text-sm px-4 py-2" style={{background: 'linear-gradient(135deg, #FF6B35, #e85a24)'}}>
            Get Started
          </Link>
        </div>
      </nav>

      {/* ── HERO HIGHLIGHT BANNER ── */}
      <section className="relative pt-16 overflow-hidden">
        {/* Background gradient layer */}
        <div className="absolute inset-0 bg-gradient-to-br from-blue-950/40 via-[#050505] to-[#FF6B35]/10 pointer-events-none" />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[900px] h-[500px] bg-blue-600/10 rounded-full blur-[140px] pointer-events-none" />
        <div className="absolute bottom-0 right-1/4 w-[400px] h-[300px] bg-[#FF6B35]/12 rounded-full blur-[100px] pointer-events-none" />

        <div className="relative max-w-5xl mx-auto px-6 md:px-12 py-20 md:py-28 text-center">
          {/* Eyebrow badge */}
          <div className="inline-flex items-center gap-2 bg-[#FF6B35]/10 border border-[#FF6B35]/30 rounded-full px-4 py-1.5 mb-6">
            <Camera size={13} className="text-[#FF6B35]" />
            <span className="text-xs font-600 text-[#FF8C5A] uppercase tracking-widest">AI-Powered Estimation</span>
          </div>

          {/* Primary headline */}
          <h1 className="text-5xl sm:text-6xl md:text-8xl font-800 leading-[1.0] tracking-tight text-balance mb-5">
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-white via-blue-100 to-white">
              Photo to Estimate
            </span>
            <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#FF6B35] via-orange-300 to-blue-400">
              in Seconds.
            </span>
          </h1>

          {/* Sub-headline */}
          <p className="text-lg md:text-2xl text-[#999] max-w-2xl mx-auto leading-relaxed mb-10 font-400">
            Clients snap a photo. AI analyzes it. You receive a scored lead with a rough estimate — instantly, before you ever pick up the phone.
          </p>

          {/* CTA row */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-14">
            <Link href="/quick-quote-form" className="flex items-center gap-2 text-base px-8 py-4 text-lg font-600 rounded-xl text-white transition-all duration-200 hover:opacity-90 active:scale-95" style={{background: 'linear-gradient(135deg, #FF6B35, #e85a24)'}}>
              <Camera size={18} />
              Try It Now — Free
            </Link>
            <Link href="/sign-up-login-screen" className="leverage-btn-secondary flex items-center gap-2 text-base px-8 py-4 text-lg font-500">
              Owner Sign In <ChevronRight size={16} />
            </Link>
          </div>

          {/* Speed proof strip */}
          <div className="inline-flex flex-wrap items-center justify-center gap-6 bg-[#0D0D0D]/80 border border-[#1E1E1E] rounded-2xl px-8 py-4">
            {[
              { icon: <Camera size={15} className="text-[#FF6B35]" />, label: 'Upload a photo' },
              { icon: <span className="text-[#FF6B35] font-700 text-sm">AI</span>, label: 'Vision analysis' },
              { icon: <DollarSign size={15} className="text-green-400" />, label: 'Instant estimate' },
              { icon: <CheckCircle size={15} className="text-blue-400" />, label: 'Scored lead delivered' },
            ].map((step, i) => (
              <React.Fragment key={i}>
                <div className="flex items-center gap-2 text-sm text-[#888]">
                  {step.icon}
                  <span>{step.label}</span>
                </div>
                {i < 3 && <ChevronRight size={12} className="text-[#333] hidden sm:block" />}
              </React.Fragment>
            ))}
          </div>
        </div>
      </section>

      {/* ── HERO ── */}
      <section className="relative pt-16 pb-24 px-6 md:px-12 overflow-hidden">
        {/* ambient glow */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[700px] h-[400px] bg-blue-600/8 rounded-full blur-[120px] pointer-events-none" />
        <div className="absolute top-20 right-0 w-[300px] h-[300px] bg-[#FF6B35]/8 rounded-full blur-[100px] pointer-events-none" />

        <div className="relative max-w-5xl mx-auto">
          <div className="inline-flex items-center gap-2 bg-[#FF6B35]/10 border border-[#FF6B35]/25 rounded-full px-3.5 py-1.5 mb-8">
            <span className="w-1.5 h-1.5 rounded-full bg-[#FF6B35] animate-pulse" />
            <span className="text-xs text-[#FF8C5A] font-500">Built for service-based businesses</span>
          </div>

          <h1 className="text-5xl md:text-7xl font-800 leading-[1.05] tracking-tight text-balance mb-6">
            Stop Quoting<br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#FF6B35] via-orange-300 to-blue-400">
              Tire-Kickers.
            </span>
          </h1>

          <p className="text-lg md:text-xl text-[#888] max-w-2xl leading-relaxed mb-10 font-400">
            Serious Quotes is an AI-powered lead filter and estimator. Clients submit a photo and answer three questions. You get a scored lead with a rough estimate — before you ever pick up the phone.
          </p>

          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <Link href="/sign-up-login-screen" className="flex items-center gap-2 text-base px-7 py-3.5 rounded-xl text-white font-600 transition-all duration-200 hover:opacity-90 active:scale-95" style={{background: 'linear-gradient(135deg, #FF6B35, #e85a24)'}}>
              Start Filtering Leads <ArrowRight size={16} />
            </Link>
            <Link href="/quick-quote-form" className="leverage-btn-secondary flex items-center gap-2 text-base px-7 py-3.5">
              See the Client Form <ChevronRight size={16} />
            </Link>
          </div>

          {/* mini proof strip */}
          <div className="flex flex-wrap items-center gap-6 mt-12 pt-8 border-t border-[#1A1A1A]">
            {[
              { icon: <Shield size={14} />, text: 'No more wasted site visits' },
              { icon: <Clock size={14} />, text: 'Instant rough estimates' },
              { icon: <TrendingUp size={14} />, text: 'AI-scored leads, ranked by value' },
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-2 text-sm text-[#666]">
                <span className="text-[#FF6B35]">{item.icon}</span>
                {item.text}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── STATS ── */}
      <section ref={statsRef} className="py-16 px-6 md:px-12 border-y border-[#1A1A1A] bg-[#080808]">
        <div className="max-w-4xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-10">
          <StatCard value={73} suffix="%" label="of quotes go to unqualified leads" start={statsVisible} />
          <StatCard value={4} suffix="hrs" label="avg time lost per bad lead" start={statsVisible} />
          <StatCard value={3} suffix="x" label="more closes with pre-scored leads" start={statsVisible} />
          <StatCard value={30} suffix="s" label="for a client to submit a quote" start={statsVisible} />
        </div>
      </section>

      {/* ── CUSTOMER WALKTHROUGH ── */}
      <section className="py-24 px-6 md:px-12">
        <div className="max-w-5xl mx-auto">
          <div className="mb-4">
            <span className="text-xs font-600 uppercase tracking-widest text-[#FF6B35]">The Client Experience</span>
          </div>
          <h2 className="text-3xl md:text-5xl font-800 tracking-tight mb-4">
            Three steps. <span className="text-[#FF6B35]">Thirty seconds.</span>
          </h2>
          <p className="text-[#666] text-lg mb-16 max-w-xl">
            Your clients get a professional, instant rough estimate. You get everything you need to decide if they're worth your time.
          </p>

          {/* Bento-style asymmetric grid */}
          <div className="grid grid-cols-1 md:grid-cols-12 gap-4">

            {/* Step 1 — large card */}
            <div className="md:col-span-5 bg-[#0D0D0D] border border-[#1F1F1F] rounded-2xl p-7 flex flex-col gap-5 hover:border-[#FF6B35]/30 transition-colors duration-300">
              <div className="w-10 h-10 rounded-xl bg-[#FF6B35]/15 border border-[#FF6B35]/25 flex items-center justify-center">
                <Camera size={20} className="text-[#FF6B35]" />
              </div>
              <div>
                <div className="text-[11px] font-600 uppercase tracking-widest text-[#444] mb-2">Step 01</div>
                <h3 className="text-xl font-700 text-white mb-2">Upload a photo</h3>
                <p className="text-[#666] text-sm leading-relaxed">
                  The client snaps a photo of the project — a room to paint, a lawn to landscape, a pipe to fix. No lengthy forms. Just a photo.
                </p>
              </div>
              {/* mock phone UI */}
              <div className="mt-auto bg-[#0A0A0A] border border-[#1A1A1A] rounded-xl p-4 flex flex-col gap-3">
                <div className="w-full h-28 rounded-lg bg-gradient-to-br from-[#1A1A1A] to-[#111] flex items-center justify-center border border-dashed border-[#2A2A2A]">
                  <div className="flex flex-col items-center gap-2 text-[#444]">
                    <Camera size={24} />
                    <span className="text-xs">photo_project.jpg</span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle size={14} className="text-[#FF6B35]" />
                  <span className="text-xs text-[#666]">Photo analyzed · 1,240 sq ft detected</span>
                </div>
              </div>
            </div>

            {/* Step 2 */}
            <div className="md:col-span-7 bg-[#0D0D0D] border border-[#1F1F1F] rounded-2xl p-7 flex flex-col gap-5 hover:border-[#FF6B35]/20 transition-colors duration-300">
              <div className="w-10 h-10 rounded-xl bg-[#FF6B35]/10 border border-[#FF6B35]/20 flex items-center justify-center">
                <MessageSquare size={20} className="text-[#FF6B35]" />
              </div>
              <div>
                <div className="text-[11px] font-600 uppercase tracking-widest text-[#444] mb-2">Step 02</div>
                <h3 className="text-xl font-700 text-white mb-2">Answer 3 Leverage Questions</h3>
                <p className="text-[#666] text-sm leading-relaxed">
                  Budget range, timeline urgency, and whether this is a must-have or nice to-have. These three answers are everything you need to score a lead.
                </p>
              </div>
              {/* mock question cards */}
              <div className="mt-auto grid grid-cols-1 sm:grid-cols-3 gap-3">
                {[
                  { label: 'Budget', value: '$1,000 – $3,000', icon: <DollarSign size={13} />, color: 'text-[#FF6B35]' },
                  { label: 'Timeline', value: 'Within 2 weeks', icon: <Clock size={13} />, color: 'text-blue-400' },
                  { label: 'Priority', value: 'Must-have', icon: <Star size={13} />, color: 'text-green-400' },
                ].map((q, i) => (
                  <div key={i} className="bg-[#111] border border-[#1A1A1A] rounded-lg p-3">
                    <div className={`flex items-center gap-1.5 text-[11px] font-600 uppercase tracking-wide mb-1.5 ${q.color}`}>
                      {q.icon} {q.label}
                    </div>
                    <div className="text-sm text-white font-500">{q.value}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Step 3 — full width */}
            <div className="md:col-span-12 bg-[#0D0D0D] border border-[#1F1F1F] rounded-2xl p-7 flex flex-col md:flex-row gap-8 hover:border-[#FF6B35]/15 transition-colors duration-300">
              <div className="flex-1 flex flex-col gap-4">
                <div className="w-10 h-10 rounded-xl bg-[#1A1A1A] border border-[#2A2A2A] flex items-center justify-center">
                  <User size={20} className="text-[#888]" />
                </div>
                <div>
                  <div className="text-[11px] font-600 uppercase tracking-widest text-[#444] mb-2">Step 03</div>
                  <h3 className="text-xl font-700 text-white mb-2">Contact info & instant estimate</h3>
                  <p className="text-[#666] text-sm leading-relaxed max-w-md">
                    The client enters their name and email. The AI immediately generates a rough estimate range based on your custom price table. They feel heard. You have a qualified lead.
                  </p>
                </div>
              </div>
              {/* mock estimate reveal */}
              <div className="flex-1 bg-[#0A0A0A] border border-[#1A1A1A] rounded-xl p-5 flex flex-col gap-4">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-[#555] uppercase tracking-widest font-600">Rough Estimate</span>
                  <span className="badge-high">High Leverage</span>
                </div>
                <div className="text-3xl font-800 text-[#FF6B35]">$1,800 – $2,400</div>
                <div className="space-y-2">
                  {[
                    { label: 'Base service fee', value: '$350' },
                    { label: '1,240 sq ft × $1.25/sqft', value: '$1,550' },
                    { label: 'Urgency variance (+10%)', value: '+$190' },
                  ].map((row, i) => (
                    <div key={i} className="flex items-center justify-between text-xs text-[#555]">
                      <span>{row.label}</span>
                      <span className="text-[#888] font-500">{row.value}</span>
                    </div>
                  ))}
                </div>
                <div className="pt-3 border-t border-[#1A1A1A] flex items-center gap-2 text-xs text-[#555]">
                  <CheckCircle size={12} className="text-[#FF6B35]" />
                  Sent to your Lead Inbox
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* ── OWNER / BACKEND WALKTHROUGH ── */}
      <section className="py-24 px-6 md:px-12 bg-[#080808] border-y border-[#1A1A1A]">
        <div className="max-w-5xl mx-auto">
          <div className="mb-4">
            <span className="text-xs font-600 uppercase tracking-widest text-[#FF6B35]">The Owner Experience</span>
          </div>
          <h2 className="text-3xl md:text-5xl font-800 tracking-tight mb-4">
            Your <span className="text-[#FF6B35]">decision dashboard.</span>
          </h2>
          <p className="text-[#666] text-lg mb-16 max-w-xl">
            Every lead is scored, ranked, and ready for a decision. No more guessing who's serious.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-4">

            {/* Lead Inbox — wide */}
            <div className="md:col-span-8 bg-[#0D0D0D] border border-[#1F1F1F] rounded-2xl p-7 flex flex-col gap-5 hover:border-[#FF6B35]/20 transition-colors duration-300">
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2.5 mb-3">
                    <div className="w-9 h-9 rounded-xl bg-[#FF6B35]/10 border border-[#FF6B35]/20 flex items-center justify-center">
                      <LayoutDashboard size={18} className="text-[#FF6B35]" />
                    </div>
                    <h3 className="text-lg font-700 text-white">Lead Inbox</h3>
                  </div>
                  <p className="text-[#666] text-sm leading-relaxed max-w-sm">
                    Every submission lands here, pre-scored. High-leverage leads float to the top. Low-value ones are flagged automatically.
                  </p>
                </div>
              </div>
              {/* mock table rows */}
              <div className="mt-2 space-y-2">
                {[
                  { name: 'Marcus T.', project: 'Interior repaint · 3BR', score: 'High', scoreClass: 'badge-high', estimate: '$2,100', time: '2m ago' },
                  { name: 'Sandra K.', project: 'Deck refinish · 400 sqft', score: 'High', scoreClass: 'badge-high', estimate: '$1,650', time: '18m ago' },
                  { name: 'Derek W.', project: 'Fence repair · small', score: 'Medium', scoreClass: 'badge-medium', estimate: '$480', time: '1h ago' },
                  { name: 'Priya M.', project: 'Bathroom tile · 60 sqft', score: 'Low', scoreClass: 'badge-low', estimate: '$320', time: '3h ago' },
                ].map((lead, i) => (
                  <div key={i} className="flex items-center justify-between bg-[#0A0A0A] border border-[#1A1A1A] rounded-lg px-4 py-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-7 h-7 rounded-full bg-[#1A1A1A] flex items-center justify-center text-[11px] font-700 text-[#666] flex-shrink-0">
                        {lead.name[0]}
                      </div>
                      <div className="min-w-0">
                        <div className="text-sm font-600 text-white truncate">{lead.name}</div>
                        <div className="text-xs text-[#555] truncate">{lead.project}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0 ml-3">
                      <span className={lead.scoreClass}>{lead.score}</span>
                      <span className="text-sm font-600 text-white hidden sm:block">{lead.estimate}</span>
                      <span className="text-xs text-[#444] hidden md:block">{lead.time}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Score breakdown — narrow */}
            <div className="md:col-span-4 flex flex-col gap-4">
              <div className="bg-[#0D0D0D] border border-[#1F1F1F] rounded-2xl p-6 flex flex-col gap-4 hover:border-[#FF6B35]/20 transition-colors duration-300">
                <div className="flex items-center gap-2.5">
                  <div className="w-9 h-9 rounded-xl bg-[#FF6B35]/10 border border-[#FF6B35]/20 flex items-center justify-center">
                    <Filter size={16} className="text-[#FF6B35]" />
                  </div>
                  <h3 className="text-base font-700 text-white">Leverage Score</h3>
                </div>
                <p className="text-[#666] text-xs leading-relaxed">
                  Three signals combine into one score: budget fit, urgency, and project intent.
                </p>
                <div className="space-y-3">
                  {[
                    { label: 'Budget Signal', pct: 85, color: 'bg-[#FF6B35]' },
                    { label: 'Urgency Signal', pct: 90, color: 'bg-blue-500' },
                    { label: 'Intent Signal', pct: 70, color: 'bg-green-500' },
                  ].map((bar, i) => (
                    <div key={i}>
                      <div className="flex justify-between text-xs text-[#555] mb-1">
                        <span>{bar.label}</span>
                        <span>{bar.pct}%</span>
                      </div>
                      <div className="h-1.5 bg-[#1A1A1A] rounded-full overflow-hidden">
                        <div className={`h-full ${bar.color} rounded-full`} style={{ width: `${bar.pct}%` }} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-[#0D0D0D] border border-[#1F1F1F] rounded-2xl p-6 flex flex-col gap-3 hover:border-[#2A2A2A] transition-colors duration-300">
                <div className="flex items-center gap-2.5">
                  <div className="w-9 h-9 rounded-xl bg-[#1A1A1A] border border-[#2A2A2A] flex items-center justify-center">
                    <BarChart2 size={16} className="text-[#888]" />
                  </div>
                  <h3 className="text-base font-700 text-white">This Week</h3>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { label: 'Leads In', value: '14' },
                    { label: 'High Score', value: '6' },
                    { label: 'Avg Estimate', value: '$1.4k' },
                    { label: 'Time Saved', value: '11h' },
                  ].map((stat, i) => (
                    <div key={i} className="bg-[#0A0A0A] border border-[#1A1A1A] rounded-lg p-3">
                      <div className="text-lg font-800 text-[#FF6B35]">{stat.value}</div>
                      <div className="text-[11px] text-[#555]">{stat.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Price Table settings card */}
            <div className="md:col-span-12 bg-[#0D0D0D] border border-[#1F1F1F] rounded-2xl p-7 flex flex-col md:flex-row gap-8 hover:border-[#2A2A2A] transition-colors duration-300">
              <div className="flex-1 flex flex-col gap-4">
                <div className="flex items-center gap-2.5">
                  <div className="w-9 h-9 rounded-xl bg-[#1A1A1A] border border-[#2A2A2A] flex items-center justify-center">
                    <Settings2 size={16} className="text-[#888]" />
                  </div>
                  <h3 className="text-lg font-700 text-white">Your Price Table</h3>
                </div>
                <p className="text-[#666] text-sm leading-relaxed max-w-sm">
                  Define your own pricing rules — base fees, per-unit rates, project types. The AI uses your table to generate every estimate. You stay in control.
                </p>
                <Link href="/business-settings" className="leverage-btn-secondary inline-flex items-center gap-2 text-sm self-start mt-auto">
                  Open Settings <ChevronRight size={14} />
                </Link>
              </div>
              {/* mock price table */}
              <div className="flex-1 bg-[#0A0A0A] border border-[#1A1A1A] rounded-xl overflow-hidden">
                <div className="grid grid-cols-3 px-4 py-2.5 border-b border-[#1A1A1A]">
                  {['Service Type', 'Base Fee', 'Per Unit'].map((h) => (
                    <span key={h} className="text-[10px] font-600 uppercase tracking-widest text-[#444]">{h}</span>
                  ))}
                </div>
                {[
                  { type: 'Interior Paint', base: '$350', unit: '$1.25/sqft' },
                  { type: 'Exterior Paint', base: '$500', unit: '$1.80/sqft' },
                  { type: 'Deck Refinish', base: '$200', unit: '$2.50/sqft' },
                  { type: 'Fence Repair', base: '$150', unit: '$8/lft' },
                ].map((row, i) => (
                  <div key={i} className="grid grid-cols-3 px-4 py-3 border-b border-[#111] hover:bg-[#111] transition-colors">
                    <span className="text-sm text-white font-500">{row.type}</span>
                    <span className="text-sm text-[#888]">{row.base}</span>
                    <span className="text-sm text-[#888]">{row.unit}</span>
                  </div>
                ))}
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* ── HOW IT CONNECTS ── */}
      <section className="py-24 px-6 md:px-12">
        <div className="max-w-5xl mx-auto">
          <div className="mb-4">
            <span className="text-xs font-600 uppercase tracking-widest text-[#FF6B35]">The Full Loop</span>
          </div>
          <h2 className="text-3xl md:text-5xl font-800 tracking-tight mb-16">
            From photo to decision<br />
            <span className="text-[#FF6B35]">in under a minute.</span>
          </h2>

          {/* Horizontal flow — not a vertical timeline */}
          <div className="relative flex flex-col md:flex-row gap-0">
            {[
              {
                step: '01',
                label: 'Client submits',
                desc: 'Photo + 3 questions + contact info. 30 seconds on mobile.',
                icon: <Camera size={18} />,
                accent: 'border-[#FF6B35]/30 bg-[#FF6B35]/5',
                iconBg: 'bg-[#FF6B35]/15 border-[#FF6B35]/25 text-[#FF6B35]',
              },
              {
                step: '02',
                label: 'AI scores & estimates',
                desc: 'Photo metadata + your price table = instant rough estimate and leverage score.',
                icon: <Zap size={18} />,
                accent: 'border-blue-600/30 bg-blue-600/5',
                iconBg: 'bg-blue-600/15 border-blue-600/25 text-blue-400',
              },
              {
                step: '03',
                label: 'You decide',
                desc: 'Open your dashboard. High-leverage leads are on top. Low-value ones are already filtered.',
                icon: <LayoutDashboard size={18} />,
                accent: 'border-[#2A2A2A] bg-[#0D0D0D]',
                iconBg: 'bg-[#1A1A1A] border-[#2A2A2A] text-[#888]',
              },
            ].map((item, i) => (
              <React.Fragment key={i}>
                <div className={`flex-1 border ${item.accent} rounded-2xl p-7 flex flex-col gap-4`}>
                  <div className="flex items-center justify-between">
                    <div className={`w-9 h-9 rounded-xl border flex items-center justify-center ${item.iconBg}`}>
                      {item.icon}
                    </div>
                    <span className="text-3xl font-800 text-[#1A1A1A]">{item.step}</span>
                  </div>
                  <div>
                    <h3 className="text-base font-700 text-white mb-1.5">{item.label}</h3>
                    <p className="text-sm text-[#666] leading-relaxed">{item.desc}</p>
                  </div>
                </div>
                {i < 2 && (
                  <div className="flex items-center justify-center md:w-8 py-4 md:py-0">
                    <ArrowRight size={16} className="text-[#2A2A2A] rotate-90 md:rotate-0" />
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="py-24 px-6 md:px-12 bg-[#080808] border-t border-[#1A1A1A]">
        <div className="max-w-3xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-[#FF6B35]/10 border border-[#FF6B35]/25 rounded-full px-3.5 py-1.5 mb-8">
            <Zap size={12} className="text-[#FF6B35]" />
            <span className="text-xs text-[#FF8C5A] font-500">$29/month · Cancel anytime</span>
          </div>
          <h2 className="text-4xl md:text-6xl font-800 tracking-tight mb-6 text-balance">
            Your time is worth<br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#FF6B35] via-orange-300 to-blue-400">
              more than free quotes.
            </span>
          </h2>
          <p className="text-[#666] text-lg mb-10 max-w-xl mx-auto leading-relaxed">
            Set up your price table once. Let the AI filter every lead. Spend your energy on clients who are ready to buy.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link href="/sign-up-login-screen" className="flex items-center gap-2 text-base px-8 py-4 rounded-xl text-white font-600 transition-all duration-200 hover:opacity-90 active:scale-95" style={{background: 'linear-gradient(135deg, #FF6B35, #e85a24)'}}>
              Create Your Account <ArrowRight size={16} />
            </Link>
            <Link href="/quick-quote-form" className="leverage-btn-secondary flex items-center gap-2 text-base px-8 py-4">
              Preview the Client Form
            </Link>
          </div>
          <p className="text-xs text-[#444] mt-6">No credit card required to start. Stripe billing activates on your first lead.</p>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer className="py-8 px-6 md:px-12 border-t border-[#1A1A1A]">
        <div className="max-w-5xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 rounded flex items-center justify-center" style={{background: 'linear-gradient(135deg, #FF6B35, #e85a24)'}}>
              <Zap size={10} className="text-white" />
            </div>
            <span className="text-xs text-[#444] font-500">Powered by Serious Leverage Inc.</span>
          </div>
          <div className="flex items-center gap-6">
            <Link href="/sign-up-login-screen" className="text-xs text-[#444] hover:text-[#FF6B35] transition-colors">Sign In</Link>
            <Link href="/quick-quote-form" className="text-xs text-[#444] hover:text-[#FF6B35] transition-colors">Client Form</Link>
            <Link href="/decision-dashboard" className="text-xs text-[#444] hover:text-[#FF6B35] transition-colors">Dashboard</Link>
          </div>
          <span className="text-xs text-[#333]">© 2026 Serious Leverage Inc.</span>
        </div>
      </footer>

    </div>
  );
}